// Lesson note templates.
//
// A saved body a teacher applies to a lesson note instead of typing the same
// practice instruction for the twentieth time. MMS has them in two places, both
// visible in the captures: a `Templates` control at the foot of the note editor
// (research/mms-capture/03-modals/cal-11-lesson-details-notes.jpg) and a
// "Lesson Note Templates" item in the calendar's Options menu (cal-13), which is
// where the list is managed. This mirrors both.
//
// What the captures do *not* settle, and what this does:
//
//   - Whether applying a template replaces the note or is inserted into it.
//     This appends, and never overwrites — see applyTemplate.
//   - Whether a template belongs to one audience. Ours do not: any of the three
//     notes can take one. MMS's control is a single unlabelled `Templates`
//     button with no audience on it.
//
// **Adapters.** Normalising, completeness, applying, the order, the preview
// line and the document are `core/src/lesson.rs`, so a phone's template picker
// appends the same way; the two bounds stay here for the forms' `maxLength`,
// pinned to the core's. `core/src/lesson_vectors.json` holds the recorded
// answers.
//
// Bodies are Markdown source held as text, like the notes they go into:
// `applyTemplate` concatenates a template body onto a note body, so the two
// have to be the same language. HTML is never stored (issue #74); Markdown is
// rendered at read time. The supported subset is
// docs/features/lesson-note-markdown.md.

// The longest strings firestore.rules accepts. The rule is written `size() <
// 100` / `size() < 65536` to match its neighbours in that file, so the longest
// accepted string is one shorter than the bound, and these are that length —
// what a form's `maxLength` should be.
//
// 65536 is the bound every note in this app gets — a family's `notes`, and each
// of the three lesson-note bodies (NOTE_BODY_MAX in domain/lessonNotes.js, the
// same 65535, where the number is argued).
//
// **The template body has the note body's bound** (issue #93): applyTemplate
// *concatenates* a template body into a note body, so the two are the same
// text in the same box (#82). MMS shows no limit on a template body.
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

export const TEMPLATE_LABEL_MAX = 99;
export const TEMPLATE_BODY_MAX = 65535;

// What gets stored. Trimmed, because a template is picked from a list by its
// label and " Scales" sorts before "Arpeggios"; bounded, because the rules
// refuse anything longer.
export function normaliseTemplate(input = {}) {
  const t = coreSync().normaliseTemplate(String(input?.label ?? ''), String(input?.body ?? ''));
  return { label: t.label, body: t.body };
}

// A template with nothing in it is not a template. Both halves are required:
// the label is how it is picked and the body is the whole point, and
// firestore.rules refuses an empty one of either.
export function isCompleteTemplate(t) {
  return coreSync().isCompleteTemplate(String(t?.label ?? ''), String(t?.body ?? ''));
}

// Applying a template to the note being written.
//
// It appends; it never replaces. A teacher who has typed two sentences and then
// reaches for "Practice assignment" wants both, and there is no undo on a
// textarea the drawer is about to save; an appended block can be deleted.
//
// Blank line between, so two applied templates do not run into one sentence,
// and no leading blank line when the note was empty.
export function applyTemplate(current, templateBody) {
  return coreSync().applyTemplate(String(current ?? ''), String(templateBody ?? ''));
}

// The order the picker and the manager both show. By label, because that is
// what a teacher is scanning for; case-insensitively, so "arpeggios" does not
// sort below "Zoom link".
export function sortTemplates(templates) {
  return coreSync().sortTemplates(templates.map((t) => String(t.label))).map((i) => templates[i]);
}

// What Save writes, or null when the template is not complete: the normalised
// label and body and the time it changed (`saveTemplate` in
// src/data/useNoteTemplates.jsx).
export function templateDocument(input = {}, serverTimestamp = undefined) {
  const rows = coreSync().templateDocument(String(input?.label ?? ''), String(input?.body ?? ''));
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

// What the picker shows under a template's label: the first line of its body,
// because two templates called "Practice" are told apart only by what is in
// them (TemplatePicker.jsx).
export function templatePreviewLine(body) {
  return coreSync().templatePreviewLine(String(body || ''));
}
